import { EPermissions } from '@common/definitions';
import { getNetworksWithPermission } from '@common/utils/permissions/get-networks-with-permission';

/**
 * Determines the correct network ID to use for updating or deleting a user.
 *
 * Logic:
 * - If logged-in user is an org admin (has ORGANISATION_USERS_UPDATE), use any network ID from the user being edited
 * - If logged-in user is a network admin (has NETWORK_USERS_UPDATE), use the network ID where:
 *   - The user being edited is a member, AND
 *   - The logged-in user has NETWORK_USERS_UPDATE permission
 */
export function getNetworkIdForUser(userId, getState) {
  const state = getState();
  const { permissions, users, networks } = state;
  // Get the networks IDs from the user's networks array - for deleting a user from list of users
  const userNetworksIds = users.items[userId]?.networks?.map((name) => {
    const networkId = Object.keys(networks.items).find((key) => networks.items[key]?.name === name);
    return networks.items[networkId];
  }).filter((n) => !!n) || [];

  const userNetworks = users.scopes.networks[userId] || userNetworksIds || [];

  if (!userNetworks.length) {
    return state.network.selected;
  }

  const hasOrganisationPermission = permissions.organisation?.includes(
    EPermissions.ORGANISATION_USERS_UPDATE
  );

  if (hasOrganisationPermission) {
    return userNetworks[0].id;
  }

  // Get all networks from state.networks.items (not loggedUser.networks which doesn't exist)
  // and add their permissions from permissions.networks
  const allNetworks = Object.values(networks.items || {});
  const loggedUserNetworksWithPermissions = allNetworks.map((network) => ({
    ...network,
    permissions: permissions.networks?.[network.id] || [],
  }));

  const networksWithPermission = getNetworksWithPermission(
    EPermissions.NETWORK_USERS_UPDATE,
    loggedUserNetworksWithPermissions
  );

  // Find a network where:
  // 1. The user being edited is a member (userNetworks)
  // 2. The logged-in user has NETWORK_USERS_UPDATE permission (networksWithPermission)
  // Use Set for efficient lookup and normalize IDs to strings for comparison
  const networkIdsWithPermission = new Set(
    networksWithPermission.map((n) => String(n.id))
  );

  const validNetwork = userNetworks.find((userNetwork) => networkIdsWithPermission.has(String(userNetwork.id))
  );

  if (validNetwork) {
    return validNetwork.id;
  }

  // Should not fall back to selected network if it's not valid
  // This fallback should only be used as a last resort
  return state.network.selected;
}
